import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { PushToggle } from "@/lib/push";

const POLL_MS = 45000;

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "ora";
    if (s < 3600) return `${Math.floor(s / 60)} min fa`;
    if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
}

export default function NotificationBell() {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const nav = useNavigate();

    const load = useCallback(async () => {
        try {
            const r = await api.get("/notifications");
            setItems(r.data.items || []);
            setUnread(r.data.unread || 0);
        } catch { /* silenzioso */ }
    }, []);

    /* eslint-disable */
    useEffect(() => {
        load();
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);
    /* eslint-enable */

    const openPanel = () => { setOpen((o) => !o); if (!open) load(); };

    const onItemClick = async (n) => {
        setOpen(false);
        if (!n.read) {
            setUnread((u) => Math.max(0, u - 1));
            setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
            api.post(`/notifications/${n.id}/read`).catch(() => {});
        }
        if (n.url) nav(n.url);
    };

    const markAll = async () => {
        setUnread(0);
        setItems((arr) => arr.map((x) => ({ ...x, read: true })));
        try { await api.post("/notifications/read-all"); } catch { /* */ }
    };

    const clearAll = async () => {
        setItems([]); setUnread(0); setOpen(false);
        try { await api.delete("/notifications/clear"); } catch { /* */ }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={openPanel}
                aria-label="Notifiche"
                title="Notifiche"
                className="relative w-9 h-9 flex items-center justify-center border border-t22252a text-tc9ccd3 hover:border-t2bb6ff/60 hover:text-tffffff hover:bg-t0e1014 transition-colors duration-150"
                data-testid="notification-bell-btn"
            >
                <Bell className="w-[18px] h-[18px]" />
                {unread > 0 && (
                    <>
                        <span className="absolute -top-1.5 -right-1.5 w-[18px] h-[18px] rounded-full bg-tff3b3b/70 animate-ping" aria-hidden="true" />
                        <span
                            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-tff3b3b text-tffffff op-mono text-[10px] font-bold leading-none border border-t090a0c"
                            data-testid="notification-badge"
                        >
                            {unread > 9 ? "9+" : unread}
                        </span>
                    </>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-[360px] max-h-[480px] overflow-hidden flex flex-col bg-t0b0d10 border border-t22252a shadow-2xl z-50"
                    data-testid="notification-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-t16181c">
                        <span className="op-label text-t2bb6ff">// NOTIFICHE</span>
                        <div className="flex items-center gap-2">
                            {unread > 0 && (
                                <button
                                    onClick={markAll}
                                    className="op-mono text-[10px] text-t8f95a3 hover:text-tffffff inline-flex items-center gap-1 transition-colors"
                                    data-testid="notifications-mark-all"
                                    title="Segna tutte come lette"
                                >
                                    <Check className="w-3 h-3" /> Tutte lette
                                </button>
                            )}
                            {items.length > 0 && (
                                <button
                                    onClick={clearAll}
                                    className="op-mono text-[10px] text-t8f95a3 hover:text-tff6b6b inline-flex items-center gap-1 transition-colors"
                                    data-testid="notifications-clear"
                                    title="Svuota"
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="px-4 py-10 text-center op-mono text-xs text-t5c6370" data-testid="notifications-empty">
                                Nessuna notifica
                            </div>
                        ) : (
                            items.map((n) => (
                                <button
                                    key={n.id}
                                    onClick={() => onItemClick(n)}
                                    className={`w-full text-left px-4 py-3 border-b border-t16181c hover:bg-t0e1014 transition-colors flex gap-3 ${n.read ? "opacity-60" : ""}`}
                                    data-testid={`notification-item-${n.id}`}
                                >
                                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-t2bb6ff"}`} />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-tffffff text-sm font-medium leading-tight truncate">{n.title}</span>
                                        <span className="block text-t8f95a3 text-xs mt-1 leading-snug">{n.body}</span>
                                        <span className="block op-mono text-[10px] text-t5c6370 mt-1">{timeAgo(n.created_at)}</span>
                                    </span>
                                </button>
                            ))
                        )}
                    </div>

                    <div className="px-4 py-2.5 border-t border-t16181c bg-t090a0c">
                        <div className="op-mono text-[10px] text-t5c6370 mb-1.5">Notifiche del browser (anche ad app chiusa)</div>
                        <PushToggle compact />
                    </div>
                </div>
            )}
        </div>
    );
}
